import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api';

/**
 * Общий QueryClient и словарь ключей.
 *
 * Клиент создаётся модулем, а не в компоненте: его дёргает socket.ts, чтобы
 * сбросить кэш после переподключения, а сокет живёт вне дерева React.
 */

/**
 * Не повторять запрос, который сервер осознанно отверг.
 *
 * 401 обрабатывает сам api (обновление токена и повтор), 403/404 не поменяются
 * от третьей попытки, 429 — это прямая просьба перестать. Ретраим только 5xx и
 * обрывы сети, где повтор реально помогает.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) {
    if (error.isNetwork) return true;
    return error.status >= 500;
  }
  return false;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      // Свежесть держит realtime, а не опрос: события приходят сокетом, и
      // рефетч на каждом фокусе окна только гоняет трафик.
      refetchOnWindowFocus: false,
      // 30 секунд — окно, в котором переключение между разделами не бьёт по
      // API повторно. Всё, что должно быть точным сразу, инвалидируется явно.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Ключи кэша.
 *
 * Собраны в одном месте, потому что инвалидируют их сразу трое: мутации,
 * обработчики WS-событий и восстановление сокета. Строка, набранная руками на
 * месте вызова, разъезжается молча — данные просто перестают обновляться.
 *
 * Префиксы устроены иерархически: `['conversations']` сбрасывает все списки
 * с любыми фильтрами, `['conversations', filters]` — конкретный.
 */
export const qk = {
  me: () => ['me'] as const,
  projects: () => ['projects'] as const,
  project: (projectId: string) => ['projects', projectId] as const,
  projectMembers: (projectId: string) => ['projects', projectId, 'members'] as const,

  conversations: () => ['conversations'] as const,
  conversationList: (filters: unknown) => ['conversations', 'list', filters] as const,
  conversation: (conversationId: string) => ['conversations', 'item', conversationId] as const,

  thread: (conversationId: string) => ['thread', conversationId] as const,
  threadSearch: (conversationId: string, query: string) =>
    ['thread', conversationId, 'search', query] as const,

  contact: (contactId: string) => ['contacts', contactId] as const,
  context: (conversationId: string) => ['context', conversationId] as const,

  sla: (projectId: string) => ['sla', projectId] as const,
  tags: (projectId: string) => ['tags', projectId] as const,
  folders: (projectId: string) => ['folders', projectId] as const,
  templates: (projectId: string) => ['templates', projectId] as const,
  bots: (projectId: string) => ['bots', projectId] as const,
  automation: (projectId: string) => ['automation', projectId] as const,
  dataSource: (projectId: string) => ['datasource', projectId] as const,

  search: (query: string, projectId: string | null) => ['search', projectId, query] as const,
  stats: (scope: string, params: unknown) => ['stats', scope, params] as const,
  users: () => ['users'] as const,
} as const;

/**
 * Всё, что могло протухнуть, пока сокет лежал.
 *
 * Ровно этот список сбрасывается при переподключении: события за время
 * разрыва потеряны безвозвратно, и список диалогов остаётся со счётчиками
 * получасовой давности. Оператор при этом видит не ошибку, а правдоподобные
 * старые данные — худший вид сбоя.
 */
export function invalidateRealtimeCaches(): void {
  void queryClient.invalidateQueries({ queryKey: qk.conversations() });
  void queryClient.invalidateQueries({ queryKey: ['thread'] });
  void queryClient.invalidateQueries({ queryKey: ['sla'] });
}
